/**
 * Studio Booking · Analytics tab — extracted Oct 2026 from the monolithic
 * `StudioBooking.jsx` as part of the code-split roadmap item. Self-contained
 * leaf: only reads `/workspaces/{ws}/studio/analytics` + `/studios`. No
 * cross-component state shared with the rest of the Studio hub.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";

export function AnalyticsTab({ ws }) {
  const [days, setDays] = useState(30);
  const [studioFilter, setStudioFilter] = useState("all");
  const [studios, setStudios] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ws) return;
    api.get(`/workspaces/${ws}/studio/studios`)
      .then((r) => setStudios(r.data?.studios || []))
      .catch(() => setStudios([]));
  }, [ws]);

  useEffect(() => {
    if (!ws) return;
    let cancelled = false;
    setLoading(true);
    const params = { days };
    if (studioFilter !== "all") params.studio_id = studioFilter;
    api.get(`/workspaces/${ws}/studio/analytics`, { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) toast.error(apiErrorMessage(e, "Analytics failed")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ws, days, studioFilter]);

  const k = data?.kpis || {};
  const paid = k.paid || 0;
  const funnelConv = k.page_views ? Math.round((paid / k.page_views) * 1000) / 10 : 0;

  return (
    <div className="space-y-4" data-testid="sb-analytics-tab">
      <div className="flex flex-wrap items-center gap-2 border border-border bg-muted/30 p-2">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Range</span>
        {[
          { d: 7,   label: "7d" },
          { d: 30,  label: "30d" },
          { d: 90,  label: "90d" },
          { d: 365, label: "1y" },
        ].map((r) => (
          <button key={r.d} onClick={() => setDays(r.d)}
                    data-testid={`sb-analytics-range-${r.d}`}
                    className={`h-7 px-3 text-[10px] uppercase tracking-widest font-semibold border rounded-none ${days === r.d ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
            {r.label}
          </button>
        ))}
        <div className="w-3" />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Studio</span>
        <select value={studioFilter} onChange={(e) => setStudioFilter(e.target.value)}
                  className="rounded-none h-7 px-2 border border-input bg-transparent text-xs"
                  data-testid="sb-analytics-studio-filter">
          <option value="all">All studios</option>
          {studios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="flex-1" />
        {loading && <span className="text-[10px] text-muted-foreground">Loading…</span>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="sb-analytics-kpis">
        <Kpi label="Total revenue" value={`₹${(k.total_revenue || 0).toLocaleString("en-IN")}`}
             sub={`avg ₹${(k.avg_booking_value || 0).toLocaleString("en-IN")}`} tone="primary" />
        <Kpi label="Bookings" value={k.total_bookings || 0}
             sub={`${k.cancellation_rate || 0}% cancelled`} />
        <Kpi label="Page views" value={k.page_views || 0}
             sub={`${funnelConv}% converted to paid`} />
        <Kpi label="Repeat customers" value={`${k.repeat_customers || 0}/${k.total_customers || 0}`}
             sub={`${k.repeat_rate || 0}% repeat rate`} tone="emerald" />
      </div>

      <AnalyticsCard title="Bookings & revenue over time">
        <TimelineChart timeline={data?.timeline || []} />
      </AnalyticsCard>

      <div className="grid md:grid-cols-2 gap-4">
        <AnalyticsCard title={studioFilter === "all" ? "By studio" : `Selected studio · ${studios.find((s) => s.id === studioFilter)?.name || ""}`}>
          <BarChartMini rows={data?.by_studio || []} keyLabel="name"
                          valA={{ key: "bookings", label: "Bookings", tone: "primary" }}
                          valB={{ key: "revenue",  label: "Revenue ₹", tone: "amber" }}
                          activeId={studioFilter === "all" ? null : studioFilter}
                          onRowClick={(r) => setStudioFilter(r.id === studioFilter ? "all" : r.id)} />
          {studioFilter !== "all" && (
            <button onClick={() => setStudioFilter("all")}
                     className="mt-3 text-[10px] text-primary hover:underline uppercase tracking-widest font-semibold"
                     data-testid="sb-analytics-clear-studio">
              ← Back to all studios
            </button>
          )}
        </AnalyticsCard>
        <AnalyticsCard title="By space">
          <BarChartMini rows={data?.by_space || []} keyLabel="name"
                          valA={{ key: "bookings", label: "Bookings", tone: "primary" }}
                          valB={{ key: "revenue",  label: "Revenue ₹", tone: "amber" }} />
        </AnalyticsCard>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <AnalyticsCard title="Status">
          <DonutList rows={data?.status_breakdown || []} palette={["#0B2545","#B8860B","#059669","#DC2626","#6366F1","#F97316"]} />
        </AnalyticsCard>
        <AnalyticsCard title="Source">
          <DonutList rows={data?.source_breakdown || []} palette={["#0B2545","#059669","#B8860B","#DC2626"]} />
        </AnalyticsCard>
        <AnalyticsCard title="Payment method">
          <DonutList rows={data?.payment_breakdown || []} palette={["#0B2545","#059669","#B8860B","#DC2626"]} />
        </AnalyticsCard>
      </div>

      <AnalyticsCard title="Widget funnel (page views → paid)">
        <FunnelBars funnel={data?.funnel || {}} />
      </AnalyticsCard>

      <AnalyticsCard title="Top customers by lifetime value">
        <TopCustomers rows={data?.top_customers || []} />
      </AnalyticsCard>
    </div>
  );
}

function Kpi({ label, value, sub, tone }) {
  const toneCls =
    tone === "primary" ? "border-primary/40 bg-primary/5" :
    tone === "emerald" ? "border-emerald-300 bg-emerald-50" :
    tone === "amber"   ? "border-amber-300 bg-amber-50" :
    "border-border bg-muted/30";
  return (
    <div className={`border p-3 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1 leading-none">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1.5">{sub}</div>}
    </div>
  );
}

function AnalyticsCard({ title, children }) {
  return (
    <div className="border border-border bg-card">
      <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        {title}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function TimelineChart({ timeline }) {
  const [hover, setHover] = useState(null); // {t, x} or null
  if (!timeline.length) return <div className="text-xs text-muted-foreground italic py-6 text-center">No data in this range.</div>;
  const maxB = Math.max(1, ...timeline.map((t) => t.bookings));
  const maxR = Math.max(1, ...timeline.map((t) => t.revenue));
  // Feb 2026 · Today marker — vertical dashed guideline so past vs
  // upcoming bookings visually split.
  const todayIso = new Date().toISOString().slice(0, 10);
  const todayIdx = timeline.findIndex((t) => t.date === todayIso);
  const todayPct = todayIdx >= 0 ? ((todayIdx + 0.5) / timeline.length) * 100 : null;
  return (
    <div className="space-y-1 relative">
      <div className="flex items-end gap-[2px] h-32 border-b border-border relative"
             onMouseLeave={() => setHover(null)}>
        {timeline.map((t, i) => {
          const bH = (t.bookings / maxB) * 100;
          const rH = (t.revenue / maxR) * 100;
          return (
            <div key={i} className="flex-1 min-w-[3px] h-full flex items-end gap-[1px] cursor-crosshair"
                  onMouseEnter={() => setHover({ t, i })}
                  onFocus={() => setHover({ t, i })}
                  tabIndex={0}>
              <div style={{ height: `${bH}%`, background: "#0B2545", width: "50%", minHeight: t.bookings ? 2 : 0 }} />
              <div style={{ height: `${rH}%`, background: "#B8860B", width: "50%", minHeight: t.revenue ? 2 : 0 }} />
            </div>
          );
        })}
        {todayPct !== null && (
          <div style={{ position: "absolute", top: 0, bottom: 0, left: `${todayPct}%`,
                          borderLeft: "1px dashed #B8860B", pointerEvents: "none" }}
                title="Today" />
        )}
        {hover && (
          <div style={{
            position: "absolute", top: 4,
            left: `${Math.min(85, (hover.i / timeline.length) * 100)}%`,
            background: "rgba(11,37,69,0.95)", color: "white",
            fontSize: 10, padding: "6px 10px", pointerEvents: "none",
            whiteSpace: "nowrap", zIndex: 5, borderLeft: "2px solid #B8860B",
          }}>
            <div style={{ fontWeight: 700 }}>{hover.t.date}</div>
            <div>Bookings: <span style={{ fontFamily: "monospace" }}>{hover.t.bookings}</span></div>
            <div>Revenue: <span style={{ fontFamily: "monospace" }}>₹{hover.t.revenue.toLocaleString("en-IN")}</span></div>
          </div>
        )}
      </div>
      <div className="flex justify-between text-[9px] text-muted-foreground pt-1">
        <span>{timeline[0]?.date}</span>
        {todayPct !== null && <span className="text-amber-700 font-semibold">↑ Today</span>}
        <span>{timeline[timeline.length - 1]?.date}</span>
      </div>
      <div className="flex items-center gap-3 text-[10px] pt-1">
        <span className="inline-flex items-center gap-1"><span style={{ background: "#0B2545", width: 10, height: 10, display: "inline-block" }} /> Bookings</span>
        <span className="inline-flex items-center gap-1"><span style={{ background: "#B8860B", width: 10, height: 10, display: "inline-block" }} /> Revenue</span>
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <span style={{ borderLeft: "1px dashed #B8860B", width: 0, height: 10, display: "inline-block" }} /> Today
        </span>
      </div>
    </div>
  );
}

function BarChartMini({ rows, keyLabel, valA, valB, onRowClick, activeId }) {
  if (!rows.length) return <div className="text-xs text-muted-foreground italic py-4 text-center">No data.</div>;
  const maxA = Math.max(1, ...rows.map((r) => r[valA.key] || 0));
  const maxB = Math.max(1, ...rows.map((r) => r[valB.key] || 0));
  const tone = (t) => t === "primary" ? "#0B2545" : t === "amber" ? "#B8860B" : "#059669";
  return (
    <div className="space-y-2">
      {rows.slice(0, 8).map((r, i) => {
        const clickable = !!onRowClick;
        const active = activeId && r.id === activeId;
        return (
          <div key={i}
                onClick={() => clickable && onRowClick(r)}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                className={`${clickable ? "cursor-pointer hover:bg-primary/5 -mx-1 px-1 py-0.5" : ""} ${active ? "bg-primary/10 -mx-1 px-1 py-0.5 border-l-2 border-primary" : ""}`}
                data-testid={r.id ? `sb-analytics-bar-${r.id}` : undefined}
                title={clickable ? "Click to drill down to this row" : undefined}>
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="font-medium truncate">{r[keyLabel]}</span>
              <span className="text-muted-foreground font-mono text-[10px]">
                {r[valA.key]} · ₹{(r[valB.key] || 0).toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex gap-[2px] h-2">
              <div className="flex-1 bg-muted overflow-hidden">
                <div style={{ width: `${(r[valA.key] / maxA) * 100}%`, background: tone(valA.tone), height: "100%" }} />
              </div>
              <div className="flex-1 bg-muted overflow-hidden">
                <div style={{ width: `${(r[valB.key] / maxB) * 100}%`, background: tone(valB.tone), height: "100%" }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DonutList({ rows, palette }) {
  if (!rows.length) return <div className="text-xs text-muted-foreground italic py-4 text-center">No data.</div>;
  const total = rows.reduce((a, b) => a + b.n, 0);
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => {
        const pct = total ? Math.round((r.n / total) * 100) : 0;
        return (
          <div key={r.key} className="text-xs">
            <div className="flex items-center justify-between mb-0.5">
              <span className="inline-flex items-center gap-1.5">
                <span style={{ background: palette[i % palette.length], width: 8, height: 8, display: "inline-block" }} />
                {r.key}
              </span>
              <span className="font-mono text-muted-foreground">{r.n} · {pct}%</span>
            </div>
            <div className="h-1.5 bg-muted overflow-hidden">
              <div style={{ width: `${pct}%`, background: palette[i % palette.length], height: "100%" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FunnelBars({ funnel }) {
  const steps = [
    { k: "view",          label: "Page view" },
    { k: "step_datetime", label: "Date/time picked" },
    { k: "step_addons",   label: "Extras chosen" },
    { k: "step_details",  label: "Details filled" },
    { k: "book_click",    label: "Book clicked" },
    { k: "paid",          label: "Paid" },
  ];
  const values = steps.map((s) => ({ ...s, n: Number(funnel[s.k] || 0) }));
  const top = Math.max(1, values[0].n);
  if (!values[0].n) return <div className="text-xs text-muted-foreground italic py-4 text-center">No funnel events tracked yet.</div>;
  return (
    <div className="space-y-1.5">
      {values.map((v, i) => {
        const pct = Math.round((v.n / top) * 100);
        return (
          <div key={v.k} className="text-xs">
            <div className="flex items-center justify-between mb-0.5">
              <span>{v.label}</span>
              <span className="font-mono text-muted-foreground">{v.n} · {pct}%</span>
            </div>
            <div className="h-2 bg-muted overflow-hidden">
              <div style={{ width: `${pct}%`, background: i === values.length - 1 ? "#059669" : "#0B2545", height: "100%" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopCustomers({ rows }) {
  if (!rows.length) return <div className="text-xs text-muted-foreground italic py-4 text-center">No customer data in this range.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border">
            {["Customer","Bookings","Revenue","Last visit"].map((h) => (
              <th key={h} className="text-left py-1.5 px-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.email} className="border-b border-border/60">
              <td className="py-1.5 px-2">
                <div className="font-medium">{c.name || "—"}</div>
                <div className="text-[10px] text-muted-foreground">{c.email}</div>
              </td>
              <td className="py-1.5 px-2 font-mono">{c.bookings}</td>
              <td className="py-1.5 px-2 font-mono">₹{c.revenue.toLocaleString("en-IN")}</td>
              <td className="py-1.5 px-2 text-muted-foreground">{(c.last_visit || "").replace("T"," ").slice(0, 16) || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
